import { useTranslation } from "react-i18next";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import CookieBanner from "../components/common/CookieBanner";
import useDocumentMeta from "../hooks/useDocumentMeta.js";
import usePageShell from "../hooks/usePageShell.js";
import usePageLanguage from "../hooks/usePageLanguage.js";
import {
  GUIDES,
  guideContentFor,
  guideHref,
  guideServedLanguage,
} from "../content/guides/registry.js";
import { GUIDES_BASE_PATH } from "../content/guides/guideModel.js";
import { alternatesFor, canonicalUrlFor } from "../content/pages/registry.js";

/**
 * /guides — the destination the footer's Guides link points at.
 *
 * It exists because the `/preview/home-v2` navigation decision put guides in
 * the footer and nowhere else, and a footer link needs somewhere to land. There
 * was no index before: guides only existed as `/guides/<slug>`, reached from
 * search or from the product page a guide belongs to.
 *
 * ── What it is, and what it deliberately is not ───────────────────────────
 * A list, built from `content/guides/registry.js`. Adding a guide adds a row
 * here; there is no second list to maintain and no way for this page to link to
 * a guide that does not exist. Each row is the guide's own title and meta
 * description — the copy the guide already carries — so nothing on this page is
 * a new claim about anything.
 *
 * It is NOT part of the production SEO architecture, and that is the whole
 * point of shipping it this way:
 *
 *   - `noindex, nofollow`, with a self-referencing canonical.
 *   - absent from `public/sitemap.xml`.
 *   - the guides' `BreadcrumbList` still walks Home → guide, not Home →
 *     Guides → guide. Changing that is a production SEO change and belongs to
 *     the decision to index this page, not to this file.
 *
 * If it is ever promoted, this is where the decision to index it gets made — a
 * sitemap entry, `index, follow`, and the middle breadcrumb `guideModel.js`
 * already reserves.
 *
 * ── It exists in both languages ───────────────────────────────────────────
 * `/guides` and `/el/guides` are both real static documents now, generated by
 * `scripts/build-static-pages.mjs`, because the guide corpus is bilingual in
 * full: a Greek reader following the footer's «Οδηγοί» must land on a Greek URL
 * rather than on an English one that then renders Greek titles.
 *
 * Which means the head here is language-aware like every other page's. The
 * canonical is THIS language's URL, and the alternates come from the page
 * registry so both halves emit the same reciprocal pair. `usePageLanguage` —
 * not i18next directly — decides which language that is, for the same reason
 * `Guide.jsx` uses it: the URL is the authority, and the head, the switcher and
 * the analytics event have to agree about what language a page is in.
 *
 * `noindex` and hreflang together are deliberate rather than contradictory: the
 * directive is the SEO policy, the alternates are the pair being honest about
 * itself. Neither asks for the other.
 */

const GuidesIndex = () => {
  const { t } = useTranslation();
  const language = usePageLanguage();

  useDocumentMeta({
    titleKey: "meta.guidesIndex.title",
    descriptionKey: "meta.guidesIndex.description",
    canonical: canonicalUrlFor(GUIDES_BASE_PATH, language),
    robots: "noindex, nofollow",
    alternates: alternatesFor(GUIDES_BASE_PATH),
  });

  usePageShell();

  // Each guide is listed in the language it can actually be READ in, and
  // linked to the URL that language lives at. All fourteen have both today, so
  // a Greek reader gets Greek titles at Greek slugs throughout. The clamp stays
  // because it is what would keep a future English-only guide listed in English
  // at its English URL, rather than linked to an `/el/` address that has never
  // been generated.
  const guides = GUIDES.map((entry) => ({
    ...guideContentFor(entry, guideServedLanguage(entry, language)),
    href: guideHref(entry, language),
  }));

  return (
    <div className="page-wrapper hv2-page guides-index-page">
      <Navbar />
      <CookieBanner />
      <main className="main">
        <section className="section">
          <div className="container">
            <div className="hv2-header">
              <p className="hv2-eyebrow">{t("guidesIndex.eyebrow")}</p>
              <h1 className="heading-50">{t("guidesIndex.heading")}</h1>
              <p className="body-20">{t("guidesIndex.intro")}</p>
            </div>

            <ul className="hv2-list">
              {guides.map((guide) => (
                <li key={guide.slug} className="hv2-item">
                  <h2 className="heading-38 hv2-item-title">
                    <a className="hv2-guide-link" href={guide.href}>
                      {guide.title}
                    </a>
                  </h2>
                  <p className="body-15 hv2-item-body">
                    {guide.metaDescription}
                  </p>
                  <a
                    className="hv2-text-link"
                    href={guide.href}
                    aria-label={`${t("guidesIndex.readMore")}: ${guide.title}`}
                  >
                    {t("guidesIndex.readMore")}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GuidesIndex;

/** Exported for the test: the index must list every registered guide, not a
 *  hand-maintained subset that quietly falls behind the registry. */
export const LISTED_GUIDES_BASE_PATH = GUIDES_BASE_PATH;
